import { Box, Button, Flex, Heading, Input, InputGroup, SimpleGrid, Text } from '@chakra-ui/react';
import { UseFormReturn, useController } from 'react-hook-form';
import { useDebouncedFieldRegistry } from '@/utils/filterUtils/useDebouncedFieldRegistry';
import { ChakraFeatherIcon } from 'src/components/ChakraFeatherIcon';
import { SelectField } from 'src/components/SelectField';
import { VideoChannelFilters } from '../../types';
import { formatDateToDateInputValue } from '@/utils/formatDateToDateInputValue';
import { InputField } from '@/components/InputField';
import { CategoryVideo, Club } from '@/model';
import { useTranslation } from 'react-i18next';
import TeamFilterSelect from '@/components/TeamFilterSelect';

export interface VideoChannelFilterBoxProps {
  filtersForm: UseFormReturn<VideoChannelFilters>;
  forceRefetch: () => void;
  clubs: Club[];
  categoryVideos: CategoryVideo[];
}

export const VideoChannelFilterBox = ({ filtersForm, clubs, categoryVideos }: VideoChannelFilterBoxProps) => {
  const { t } = useTranslation();
  const { register, reset, control } = filtersForm;

  if (!categoryVideos.some(category => category.uuid === 'LIVE_VIDEO_CATEGORY')) {
    categoryVideos.push(
      Object.assign(new CategoryVideo(), {
        uuid: 'LIVE_VIDEO_CATEGORY',
        title: t('pages.application_videos_channel.components.video_channel_filter_box.video_channel_filter_live_video_category'),
      }),
    );
  }

  const startDateControl = useController({
    name: 'start_date',
    control,
  });
  const endDateControl = useController({
    name: 'end_date',
    control,
  });

  return (
    <Box>
      <Flex align='center' gap='pico' mb='deka'>
        <ChakraFeatherIcon color='text.main' name='filter' />
        <Heading variant='sectionTitle'>
          {t('pages.application_videos_channel.components.video_channel_filter_box.video_channel_filter_title')}
        </Heading>
      </Flex>

      <Flex direction={{ base: 'row' }} gap='small' rowGap='micro'>
        <SimpleGrid
          gap='deka'
          alignItems='flex-end'
          columns={{ base: 1, sm: 2, md: 4, lg: 5, xl: 6 }}
          rowGap='xxsmall'
          flexGrow={1}
        >
          <TeamFilterSelect showFlag name='team_uuid' control={filtersForm.control} clubs={clubs} width='100%' />

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_videos_channel.components.video_channel_filter_box.video_channel_filter_title_label')}
            </Text>
            <Input variant='flushed' {...useDebouncedFieldRegistry(() => register('title'))} />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_videos_channel.components.video_channel_filter_box.video_channel_filter_category_label')}
            </Text>
            <SelectField {...register('category_uuid')}>
              <option value=''>
                {t(
                  'pages.application_videos_channel.components.video_channel_filter_box.video_channel_filter_category_default_option',
                )}
              </option>
              {categoryVideos?.map(item => (
                <option key={item.uuid} value={item.uuid}>
                  {item.title}
                </option>
              ))}
            </SelectField>
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_videos_channel.components.video_channel_filter_box.video_channel_filter_start_date_label')}
            </Text>
            <InputField
              type='date'
              fullDatePickerArea
              variant='flushed'
              value={formatDateToDateInputValue(startDateControl.field.value)}
              onChange={event => startDateControl.field.onChange(event.target.value)}
            />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_videos_channel.components.video_channel_filter_box.video_channel_filter_end_date_label')}
            </Text>
            <InputField
              type='date'
              fullDatePickerArea
              variant='flushed'
              value={formatDateToDateInputValue(endDateControl.field.value)}
              onChange={event => endDateControl.field.onChange(event.target.value)}
              min={formatDateToDateInputValue(startDateControl.field.value)}
            />
          </InputGroup>
        </SimpleGrid>

        <Button alignSelf={{ lg: 'end' }} colorScheme='text' variant='outline' mt='10px' onClick={() => reset()}>
          {t('pages.application_videos_channel.components.video_channel_filter_box.video_channel_filter_clear_button')}
        </Button>
      </Flex>
    </Box>
  );
};
